/**
 * The copy the patient portal shows for a grid report, as HTML, so the review
 * page edits exactly what the patient sees.
 *
 * Mirrors gridHtml / personalizedPlan / actionPlanSummaryHtml in the patient
 * portal (Swimscore_patient_portal/src/lib/reportCopy.ts). Keep the two in step.
 * The portal's precedence is: admin edit (accounts.PatientData) > grid copy
 * with the AI personalization applied.
 */

const escapeHtml = (text) => String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const para = (text) => `<p>${escapeHtml(text)}</p>`;
const boldPara = (text) => `<p><strong>${escapeHtml(text)}</strong></p>`;
const joinSentences = (text, added) => (added ? `${text} ${added}` : text);

function personalizedPlan(plan, ai) {
  if (!plan || !ai) return plan;
  const bullets = plan.summary.bullets.map((b) => {
    const reason = b.code ? ai.reasons?.[b.code] : undefined;
    return reason && b.action ? { ...b, reason, text: `${b.action} ${reason}` } : b;
  });
  if (ai.extra_bullet) bullets.push({ slot: null, text: ai.extra_bullet });
  return { ...plan, summary: { ...plan.summary, intro: joinSentences(plan.summary.intro, ai.intro_addition), bullets } };
}

function actionPlanSummaryHtml(plan) {
  // Every bullet: cards an admin hides in the portal are not known here.
  const { bullets } = plan.summary;
  const list = bullets.length ? `<ul>${bullets.map((b) => `<li>${escapeHtml(b.text)}</li>`).join('')}</ul>` : '';
  return [
    para(plan.summary.intro),
    bullets.length ? `<p><strong>${escapeHtml(plan.summary.heading)}</strong></p>${list}` : '',
    para(plan.summary.closing),
  ].join('');
}

/** Plain report text (blank-line paragraphs) as HTML. */
export const plainToHtml = (text) =>
  (text || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');

/** {home_top, results_summary, results_full, action_plan} as the portal renders them, before admin edits. */
export function portalDefaultHtml(report) {
  const copy = report.patient_copy;
  // The portal shows the plain fields once the old review-page edit was used.
  if (!copy || report.copy_edited_by_admin) {
    return {
      home_top: plainToHtml(report.home_top),
      results_summary: plainToHtml(report.results_summary),
      results_full: plainToHtml(report.results_full),
      action_plan: plainToHtml(report.action_plan),
    };
  }
  const ai = copy.personalized || {};
  const [lead, ...rest] = copy.results_paragraphs;
  const full = [...rest.map(para), boldPara(copy.closing_line)];
  if (ai.results_full) full.splice(1, 0, para(ai.results_full));
  const hormoneText = copy.hormone ? [copy.hormone.text, copy.hormone.addon].filter(Boolean).join(' ') : '';
  if (copy.hormone && hormoneText) full.push(`<h4>${escapeHtml(copy.hormone.heading)}</h4>`, para(hormoneText));
  const plan = personalizedPlan(copy.action_plan, ai.action_plan);
  return {
    home_top: [boldPara(copy.headline), para(copy.home_body), ai.home_top ? para(ai.home_top) : '', boldPara(copy.closing_line)].join(''),
    results_summary: [boldPara(copy.headline), lead ? para(joinSentences(lead, ai.results_summary)) : ''].join(''),
    results_full: full.join(''),
    action_plan: plan ? actionPlanSummaryHtml(plan) : plainToHtml(report.action_plan),
  };
}
